import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryGroup, QueryRule, QueryField } from '../../types/QueryBuilder';
import { generateId, isRuleValid } from '../../utils/queryBuilderUtils';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';
import { useBrandConfig } from '../../context/BrandConfigContext';
import {
    COUNTER_FIELD_NAME,
    COUNTER_TYPE_FIELD_NAME,
    IDENTIFIER_LIST_FIELD_NAME,
} from '../../pages/Clients/QueryBuilder/components/SimpleQuery/fieldConfig';

const SIMPLE_GROUP_ID = 'simple-group';

const DEFAULT_OPTION_NAME_BY_FIELD: Record<string, string> = {
    CUSTOMER_ACCOUNT_TYPE: 'point',
};

// Champs qui ne font que qualifier un autre champ (ex: l'unité "point" du compteur fidélité) : ils ont
// une valeur par défaut, on ne les envoie donc que si le champ qualifié est renseigné.
const QUALIFIED_FIELD_BY_QUALIFIER: Record<string, string> = {
    [COUNTER_TYPE_FIELD_NAME]: COUNTER_FIELD_NAME,
};

const keepEffectiveRules = (rules: QueryRule[]): QueryRule[] => rules.filter(rule => {
    const qualifiedField = QUALIFIED_FIELD_BY_QUALIFIER[rule.field ?? ''];
    return !qualifiedField || rules.some(other => other.field === qualifiedField && isRuleValid(other));
});

const resolveDefaultValue = (field: QueryField): string => {
    const defaultOptionName = DEFAULT_OPTION_NAME_BY_FIELD[field.name]?.toLowerCase();
    if (!defaultOptionName) return '';
    const match = field.values?.find(v => v.name.toLowerCase() === defaultOptionName)
        ?? field.values?.find(v => v.label.toLowerCase() === defaultOptionName);
    return match?.name ?? '';
};

const resolveConfigDefaultIdentifiers = (field: QueryField, configuredIds: string[] | undefined): string[] | undefined => {
    if (field.name !== IDENTIFIER_LIST_FIELD_NAME) return undefined;
    if (!configuredIds?.length || !field.values?.length) return undefined;
    const matched = field.values.filter(v => configuredIds.includes(v.name)).map(v => v.name);
    return matched.length > 0 ? matched : undefined;
};

const createRuleForField = (field: QueryField, configuredIdentifierIds?: string[]): QueryRule => {
    const configDefault = resolveConfigDefaultIdentifiers(field, configuredIdentifierIds);
    if (configDefault) {
        return { id: generateId(), field: field.name, operator: 'in', value: configDefault };
    }
    return {
        id: generateId(),
        field: field.name,
        operator: (field.defaultOperator as QueryRule['operator']) || '=',
        value: resolveDefaultValue(field),
    };
};

export const useSimpleQueryBuilder = () => {
    const { simpleFields } = useQueryBuilderData();
    const { ready: brandReady, setting } = useBrandConfig();

    // Types d'identifiants cochés par défaut pour l'enseigne (query.identifiers.default.checked)
    const configuredIdentifierIds = useMemo(
        () => (brandReady ? setting('query.identifiers.default.checked') : undefined),
        [brandReady, setting]
    );

    const [rules, setRules] = useState<QueryRule[]>([]);

    useEffect(() => {
        setRules(prevRules =>
            simpleFields.map(field =>
                prevRules.find(rule => rule.field === field.name) || createRuleForField(field, configuredIdentifierIds)
            )
        );
    }, [simpleFields, configuredIdentifierIds]);

    useEffect(() => {
        if (!configuredIdentifierIds?.length) return;
        setRules(prevRules => prevRules.map(rule => {
            if (rule.field !== IDENTIFIER_LIST_FIELD_NAME || rule.value !== '') return rule;
            const field = simpleFields.find(f => f.name === rule.field);
            const configDefault = field && resolveConfigDefaultIdentifiers(field, configuredIdentifierIds);
            return configDefault ? { ...rule, operator: 'in' as const, value: configDefault } : rule;
        }));
    }, [configuredIdentifierIds, simpleFields]);

    const updateField = useCallback((fieldName: string, data: Partial<QueryRule>) => {
        setRules(prevRules =>
            prevRules.map(rule => (rule.field === fieldName ? { ...rule, ...data } : rule))
        );
    }, []);

    const resetFields = useCallback(() => {
        setRules(simpleFields.map(field => createRuleForField(field, configuredIdentifierIds)));
    }, [simpleFields, configuredIdentifierIds]);

    const effectiveRules = useMemo(() => keepEffectiveRules(rules), [rules]);

    const groups = useMemo<QueryGroup[]>(
        () => [{ id: SIMPLE_GROUP_ID, combinator: 'AND', rules: effectiveRules }],
        [effectiveRules]
    );

    const isValid = useMemo(() => effectiveRules.some(isRuleValid), [effectiveRules]);

    return {
        simpleFields,
        rules,
        groups,
        isValid,
        updateField,
        resetFields,
    };
};
